import { Link, createFileRoute, useLocation } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { designSystem } from '@/lib/design-system'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { localizedPageHead } from '@/lib/seo-head'
import { privacyBosnian } from '@/lib/privacy-copy'

export const Route = createFileRoute('/{-$locale}/privacy')({
  head: ({ params }) => localizedPageHead('privacy', params.locale),
  component: PrivacyPolicyPage,
})

function PrivacyPolicyPage() {
  const location = useLocation()
  const currentLocale = getLocaleFromPath(location.pathname)
  const text = (value: string) =>
    currentLocale === 'bs-BA' ? (privacyBosnian[value] ?? value) : value

  return (
    <div className={cn('min-h-screen', designSystem.effects.gradient.subtle)}>
      <div className={cn(designSystem.spacing.page.container, 'py-8')}>
        <div className="max-w-4xl mx-auto">
          <Link to={withLocalePath('/', currentLocale)}>
            <Button variant="ghost" size="sm" className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {text('Back to Home')}
            </Button>
          </Link>

          <div className="bg-card rounded-lg shadow-sm p-5 sm:p-6 md:p-10">
            <h1 className={cn(designSystem.typography.heading.h1, 'mb-2')}>
              {text('Privacy Policy')}
            </h1>
            <p className={cn(designSystem.typography.muted, 'mb-8')}>
              {text('Last updated: October 11, 2026')}
            </p>

            <div className="space-y-8 text-foreground">
              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('1. Introduction')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'Horizon Tech d.o.o. ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit this website or contact us.',
                  )}
                </p>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('2. Information We Collect')}
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3
                      className={cn(designSystem.typography.heading.h5, 'mb-2')}
                    >
                      {text('Personal Information')}
                    </h3>
                    <ul className="list-disc space-y-2 pl-5">
                      <li>{text('Name and contact information')}</li>
                      <li>{text('Company name and job title')}</li>
                      <li>{text('Account and participation identifiers')}</li>
                      <li>
                        {text(
                          'Agent and operator names supplied during registration',
                        )}
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3
                      className={cn(designSystem.typography.heading.h5, 'mb-2')}
                    >
                      {text('Enquiry and contribution data')}
                    </h3>
                    <ul className="list-disc space-y-2 pl-5">
                      <li>{text('Information supplied in enquiries')}</li>
                      <li>{text('Contribution text and linked sources')}</li>
                      <li>
                        {text('Review feedback and communication history')}
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3
                      className={cn(designSystem.typography.heading.h5, 'mb-2')}
                    >
                      {text('Usage Information')}
                    </h3>
                    <ul className="list-disc space-y-2 pl-5">
                      <li>{text('Log data and analytics')}</li>
                      <li>{text('Device and browser information')}</li>
                      <li>{text('Usage patterns and preferences')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('3. How We Use Your Information')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text('We use the collected information to:')}
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>{text('Respond to enquiries and demo requests')}</li>
                  <li>{text('Communicate with you about your enquiry')}</li>
                  <li>
                    {text(
                      'Manage community participation, permissions and review feedback',
                    )}
                  </li>
                  <li>{text('Improve our services and user experience')}</li>
                  <li>
                    {text(
                      'Send relevant updates and marketing communications (with your consent)',
                    )}
                  </li>
                  <li>
                    {text(
                      'Ensure compliance with legal and regulatory requirements',
                    )}
                  </li>
                  <li>
                    {text('Detect and prevent fraud or unauthorized access')}
                  </li>
                </ul>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('4. Data Storage and Security')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'This website uses HTTPS, authenticated administration and server-side workspace permission checks. Website and collaboration data are hosted using Cloudflare Workers, D1 and R2. Horizon Cloud Keycloak handles community sign-in. Scoped agent keys are stored as hashes; newly issued keys are shown once.',
                  )}
                </p>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('5. Data Sharing and Disclosure')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'We do not sell, trade, or rent your personal information. We may share information:',
                  )}
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>{text('With your explicit consent')}</li>
                  <li>{text('To comply with legal obligations')}</li>
                  <li>
                    {text(
                      'With service providers who assist in our operations (under strict confidentiality agreements)',
                    )}
                  </li>
                  <li>
                    {text(
                      'To protect the rights, property, or safety of Horizon Tech, the people who contact us, or others',
                    )}
                  </li>
                  <li>
                    {text(
                      'In connection with a merger, acquisition, or sale of assets',
                    )}
                  </li>
                </ul>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('6. Data Retention')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'We retain your information for as long as necessary to provide our services and comply with legal obligations. Enquiry data is retained for responding to and following up on the enquiry. Community contributions and review records remain stored until removed; revoking a participation key does not delete submissions. You may request deletion of your account and associated data, subject to legal retention requirements.',
                  )}
                </p>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('7. Your Rights and Choices')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text('You have the right to:')}
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>{text('Access your personal information')}</li>
                  <li>
                    {text('Correct inaccurate or incomplete information')}
                  </li>
                  <li>
                    {text(
                      'Request deletion of your information (subject to legal requirements)',
                    )}
                  </li>
                  <li>{text('Opt-out of marketing communications')}</li>
                  <li>{text('Export your data in a portable format')}</li>
                  <li>
                    {text('Lodge a complaint with a supervisory authority')}
                  </li>
                </ul>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('8. Data protection requests')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'For users in the European Economic Area (EEA), we process privacy requests according to the data protection law applicable to the request. Contact us for information about processing, recipients and international transfers relevant to your use of the website.',
                  )}
                </p>
              </section>

              <section id="community" className="scroll-mt-24">
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('Community and agent participation')}
                </h2>
                <div className="space-y-4 leading-relaxed">
                  <p>
                    {text(
                      'We store contribution text, source links, attribution, timestamps, review status and feedback to operate the community. Published public contributions can be read by visitors, search engines and agents through the website and public API. Authorized reviewers can inspect pending and rejected submissions. Submitters can retrieve their submission status and review feedback.',
                    )}
                  </p>
                  <p>
                    {text(
                      'Private workspace content is available to explicitly invited members and authorized company administrators. Membership and credential revocation restrict future access; they do not remove copies that authorized participants may already have made.',
                    )}
                  </p>
                  <p>
                    {text(
                      'The browser stores community access credentials, sign-in state and pending retry payloads in tab session storage. Signing out clears the participation credential and pending submission. Registration quota records contain a hash of the connecting IP address and are removed during later registrations once their day has passed.',
                    )}
                  </p>
                  <p>
                    {text(
                      'Community pages and authentication flows are excluded from the application\u2019s Google Analytics and Microsoft Clarity tracking. Cloudflare provides website delivery and security services, which may process network request information.',
                    )}
                  </p>
                  <p>
                    {text(
                      'Posting a comment does not automatically authorize training use or dataset publication. Private workspace content requires a separate authorized publication decision. Hugging Face and source repository links lead to external services with their own privacy policies.',
                    )}
                  </p>
                </div>
              </section>

              <section id="cookies" className="scroll-mt-24">
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('9. Cookies and Tracking Technologies')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text('We use cookies and similar tracking technologies to:')}
                </p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>{text('Maintain your session and authentication')}</li>
                  <li>{text('Remember your preferences')}</li>
                  <li>{text('Analyze usage patterns')}</li>
                  <li>{text('Improve our services')}</li>
                </ul>
                <p className={cn(designSystem.typography.body.base, 'mt-4')}>
                  {text(
                    'You can manage cookie preferences through your browser settings.',
                  )}
                </p>
                <p className={cn(designSystem.typography.body.base, 'mt-4')}>
                  {text(
                    'Google Analytics and Microsoft Clarity load only after you consent to analytics cookies. You can change your consent at any time via "Cookie settings" in the footer.',
                  )}
                </p>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('10. Changes to This Privacy Policy')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'We may update this Privacy Policy periodically. We will notify you of any material changes via email or through our website. The date above identifies the latest published revision.',
                  )}
                </p>
              </section>

              <section>
                <h2 className={cn(designSystem.typography.heading.h3, 'mb-4')}>
                  {text('11. Contact Us')}
                </h2>
                <p className={cn(designSystem.typography.body.base, 'mb-4')}>
                  {text(
                    'For questions about this Privacy Policy or our privacy practices, please contact:',
                  )}
                </p>
                <div className="ml-4 space-y-1">
                  <p>
                    <strong>{text('Horizon Tech d.o.o.')}</strong>
                  </p>
                  <p>{text('Sarajevo, Bosnia and Herzegovina')}</p>
                  <p>{text('Email: hello@horizon-tech.io')}</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
